import React, { useState, useMemo, useCallback } from 'react';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  Paper, 
  IconButton, 
  Chip, 
  Collapse, 
  TextField, 
  InputAdornment,
  Tooltip,
  Typography,
  CircularProgress,
  Button,
  Snackbar,
  Checkbox
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { KeyboardArrowDown, KeyboardArrowUp, Search, Refresh, Error as ErrorIcon, Add, PlayArrow, Edit, Pause, ArrowUpward, ArrowDownward, UnfoldMore } from '@material-ui/icons';
import useMezzanineData from '../../hooks/mezzanine/useMezzanineData';
import useExecutionTracker from '../../hooks/mezzanine/useExecutionTracker';
import CreateDeploymentForm from './CreateDeploymentForm';
import EditChannelDialog from './EditChannelDialog';
import { executeStateMachine } from '../../utilities/mezzanineApi';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  tableContainer: {
    marginBottom: theme.spacing(3),
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  deploymentRow: {
    backgroundColor: '#fff8f0',
    borderBottom: '8px solid #fff',
    '&:hover': {
      backgroundColor: '#fff0e0',
    },
  },
  nestedTable: {
    margin: theme.spacing(1),
    backgroundColor: '#fafafa',
    borderRadius: '8px',
  },
  // A channel row is as tall as its output list, so adjacent channels blur
  // together without help. Override MUI's default 1px cell border with a
  // heavier rule and top-align every cell, so the channel name sits level with
  // its first output line instead of floating in the middle of a tall row.
  channelRow: {
    '& > td': {
      verticalAlign: 'top',
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
      borderBottom: '2px solid #d0d0d0',
    },
    '&:last-child > td': {
      borderBottom: 'none',
    },
  },
  searchBox: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusChip: {
    fontWeight: 'bold',
    fontSize: '11px',
  },
  regionChip: {
    fontWeight: 'bold',
    fontSize: '10px',
    height: '22px',
  },
  priorityChip: {
    fontSize: '10px',
    height: '20px',
  },
  // One row per output, five fixed columns, so type / priority / region /
  // endpoint line up across every output on every channel. Chip height and
  // width are normalised here so SRT and UDP rows sit on the same baseline.
  outputGrid: {
    display: 'inline-grid',
    gridTemplateColumns: '38px 46px 46px auto 20px',
    columnGap: theme.spacing(0.75),
    rowGap: theme.spacing(0.5),
    alignItems: 'center',
    '& .MuiChip-root': {
      height: '20px',
      width: '100%',
    },
    '& .MuiChip-label': {
      paddingLeft: '6px',
      paddingRight: '6px',
    },
  },
  // Deliberately neutral: every tint in this file already carries meaning
  // (region, priority, status), so a coloured type tag would compete with them.
  // SRT is the primary output and sits darker; UDP is secondary and lighter.
  outputType: {
    fontSize: '9px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    lineHeight: 1,
    textAlign: 'center',
    padding: '4px 0',
    borderRadius: '3px',
    color: 'white',
  },
  outputTypeSrt: {
    backgroundColor: '#5f6368',
  },
  outputTypeUdp: {
    backgroundColor: '#9aa0a6',
  },
  // Outlined rather than solid, so a channel with no SRT output does not get a
  // confident-looking tag next to the words "No SRT output".
  outputTypeAbsent: {
    backgroundColor: 'transparent',
    border: '1px dashed #ccc',
    color: '#bbb',
    padding: '3px 0',
  },
  outputEndpoint: {
    fontSize: '11px',
    padding: '2px 8px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: 'white',
    whiteSpace: 'nowrap',
    justifySelf: 'start',
  },
  outputNotApplicable: {
    fontSize: '11px',
    color: '#ccc',
    textAlign: 'center',
  },
  errorIcon: {
    color: '#f44336',
    fontSize: '18px',
    marginLeft: theme.spacing(0.5),
  },
  summaryBox: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(1.5),
    borderRadius: '8px',
    minWidth: '120px',
    textAlign: 'center',
  },
}));

const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
    case 'CREATED':
      return { bg: '#e8f5e9', color: '#2e7d32' };  // Green
    case 'DEPLOYING':
      return { bg: '#fff3e0', color: '#e65100' };  // Orange
    case 'PENDING':
      return { bg: '#e3f2fd', color: '#1565c0' };  // Blue
    case 'FAILED':
      return { bg: '#ffebee', color: '#c62828' };  // Red
    case 'HIBERNATED':
      return { bg: '#eceff1', color: '#546e7a' };  // Blue Gray
    case 'STOPPED':
      return { bg: '#fafafa', color: '#616161' };  // Gray
    case 'DELETING':
      return { bg: '#fce4ec', color: '#c2185b' };  // Pink
    default:
      return { bg: '#fafafa', color: '#616161' };
  }
};

const getRegionColor = (region) => {
  const colors = {
    'lhr': { bg: '#e3f2fd', color: '#1565c0' },
    'fra': { bg: '#fff3e0', color: '#e65100' },
    'pdx': { bg: '#e8f5e9', color: '#2e7d32' },
    'iad': { bg: '#f3e5f5', color: '#7b1fa2' },
  };
  return colors[region?.toLowerCase()] || { bg: '#fafafa', color: '#616161' };
};

const getPriorityColor = (priority) => {
  return priority?.toLowerCase() === 'prim' 
    ? { bg: '#e8f5e9', color: '#2e7d32' }
    : { bg: '#fff3e0', color: '#e65100' };
};

const getDeploymentStatus = (deployment) => {
  const hasErrors = deployment.channels.some(ch =>
    ch.status === 'FAILED' || ch.outputs?.some(o => o.error_message)
  );
  const nonHibernatedChannels = deployment.channels.filter(ch => ch.status !== 'HIBERNATED');
  const hasHibernated = deployment.channels.some(ch => ch.status === 'HIBERNATED');
  const allHibernated = deployment.channels.every(ch => ch.status === 'HIBERNATED');
  const allActive = nonHibernatedChannels.length > 0 && nonHibernatedChannels.every(ch => 
    ch.status === 'ACTIVE' && ch.outputs?.length > 0 && ch.outputs.every(o => o.status === 'CREATED')
  );
  const isPartial = hasHibernated && !allHibernated && allActive && !hasErrors;

  if (allActive && !hasHibernated) return 'Active';
  if (hasErrors) return 'Has Errors';
  if (allHibernated) return 'Hibernated';
  if (isPartial) return 'Partial';
  return 'In Progress';
};

const Overview = () => {
  const classes = useStyles();
  const [expandedDeployments, setExpandedDeployments] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortDirection, setSortDirection] = useState(null); // null = unsorted, 'asc', 'desc'
  const [sortColumn, setSortColumn] = useState(null); // 'deployment', 'group', 'channels', 'status'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [enabledChannels, setEnabledChannels] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deployingChannels, setDeployingChannels] = useState({}); // Track channels being deployed (API call in progress)
  const [hibernatingChannels, setHibernatingChannels] = useState({}); // Track channels being hibernated (API call in progress)

  // Fetch data from API
  const { channels, outputsByChannelId, loading, error, refetch } = useMezzanineData();

  // Handle execution completion
  const handleExecutionComplete = useCallback((result) => {
    // Clear the deploying/hibernating state for this channel
    setDeployingChannels(prev => {
      const updated = { ...prev };
      delete updated[result.channelId];
      return updated;
    });
    setHibernatingChannels(prev => {
      const updated = { ...prev };
      delete updated[result.channelId];
      return updated;
    });

    if (result.success) {
      setSnackbar({ 
        open: true, 
        message: `Channel ${result.action} completed successfully`, 
        severity: 'success' 
      });
    } else {
      setSnackbar({ 
        open: true, 
        message: `Channel ${result.action} failed: ${result.error}`, 
        severity: 'error' 
      });
    }
    refetch();
  }, [refetch]);

  // Execution tracker hook
  const { startTracking, isExecuting, getExecution } = useExecutionTracker(handleExecutionComplete);

  // Toggle channel enabled state
  const handleToggleEnabled = (channelId) => {
    setEnabledChannels(prev => ({ ...prev, [channelId]: !prev[channelId] }));
  };

  // Handle edit button click
  const handleEdit = (channel) => {
    setEditingChannel(channel);
    setEditDialogOpen(true);
  };

  // Handle deploy button click
  const handleDeploy = async (channel) => {
    const channelId = channel.channel_id;
    
    if (isExecuting(channelId) || deployingChannels[channelId]) return;

    // Immediately disable the button
    setDeployingChannels(prev => ({ ...prev, [channelId]: true }));

    try {
      const response = await executeStateMachine('deploy-channel', { channel_id: channelId });
      startTracking(channelId, response.execution_arn, 'deploy');
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: `Failed to start deploy: ${err.message}`, 
        severity: 'error' 
      });
      // Re-enable button on error
      setDeployingChannels(prev => ({ ...prev, [channelId]: false }));
    }
  };

  // Handle hibernate button click
  const handleHibernate = async (channel) => {
    const channelId = channel.channel_id;
    
    if (isExecuting(channelId) || hibernatingChannels[channelId]) return;

    // Immediately disable the button
    setHibernatingChannels(prev => ({ ...prev, [channelId]: true }));

    try {
      const response = await executeStateMachine('hibernate-channel', { channel_id: channelId });
      startTracking(channelId, response.execution_arn, 'hibernate');
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: `Failed to start hibernate: ${err.message}`, 
        severity: 'error' 
      });
      // Re-enable button on error
      setHibernatingChannels(prev => ({ ...prev, [channelId]: false }));
    }
  };

  // Handle auto-deploy executions from create form
  const handleAutoDeployExecutions = useCallback((executionsList) => {
    executionsList.forEach(({ channelId, executionArn }) => {
      startTracking(channelId, executionArn, 'deploy');
    });
  }, [startTracking]);

  // Group channels by deployment_name
  const deploymentGroups = useMemo(() => {
    const groups = {};
    channels.forEach(channel => {
      const deploymentName = channel.deployment_name;
      if (!groups[deploymentName]) {
        groups[deploymentName] = {
          deployment_name: deploymentName,
          eid: channel.eid,
          group_name: channel.group_name,
          channels: []
        };
      }
      // Attach output data to channel (supports multiple outputs per channel)
      const channelOutputs = outputsByChannelId[channel.channel_id] || [];
      groups[deploymentName].channels.push({
        ...channel,
        outputs: channelOutputs,
        // A channel can feed several EVIS ingresses, so SRT outputs are a list.
        // Each output carries its own stream_id, region and stream_priority -
        // the matching channel fields are legacy copies and may be null.
        srtOutputs: channelOutputs.filter(o => o.output_type === 'SRT_CALLER'),
        udpOutput: channelOutputs.find(o => o.output_type === 'UDP') || null,
      });
    });
    return Object.values(groups);
  }, [channels, outputsByChannelId]);

  // Filter deployments based on search query
  const filteredDeployments = useMemo(() => {
    if (!searchQuery) return deploymentGroups;
    const query = searchQuery.toLowerCase();
    return deploymentGroups.filter(deployment => {
      if (deployment.deployment_name.toLowerCase().includes(query)) return true;
      if (deployment.eid?.toLowerCase().includes(query)) return true;
      return deployment.channels.some(ch =>
        ch.channel_name?.toLowerCase().includes(query) ||
        ch.device_id?.toLowerCase().includes(query) ||
        ch.tx_chain_name?.toLowerCase().includes(query) ||
        // region, stream_priority and stream_id live on the outputs now, so
        // search those rather than the legacy channel copies.
        ch.outputs?.some(o =>
          o.region?.toLowerCase().includes(query) ||
          o.stream_priority?.toLowerCase().includes(query) ||
          o.stream_id?.toLowerCase().includes(query) ||
          o.output_name?.toLowerCase().includes(query)
        )
      );
    });
  }, [deploymentGroups, searchQuery]);

  // Sort deployments
  const sortedDeployments = useMemo(() => {
    if (!sortDirection || !sortColumn) return filteredDeployments;
    return [...filteredDeployments].sort((a, b) => {
      let valA, valB;
      switch (sortColumn) {
        case 'deployment':
          valA = a.deployment_name.toLowerCase();
          valB = b.deployment_name.toLowerCase();
          return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'group':
          valA = (a.group_name || '').toLowerCase();
          valB = (b.group_name || '').toLowerCase();
          return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'channels':
          valA = a.channels.length;
          valB = b.channels.length;
          return sortDirection === 'asc' ? valA - valB : valB - valA;
        case 'status':
          valA = getDeploymentStatus(a);
          valB = getDeploymentStatus(b);
          return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        default:
          return 0;
      }
    });
  }, [filteredDeployments, sortDirection, sortColumn]);

  const handleSortToggle = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => {
        if (prev === 'asc') return 'desc';
        setSortColumn(null);
        return null;
      });
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Calculate summary stats
  const stats = useMemo(() => {
    return {
      total: channels.length,
      active: channels.filter(c => c.status === 'ACTIVE').length,
      deploying: channels.filter(c => c.status === 'DEPLOYING').length,
      failed: channels.filter(c => c.status === 'FAILED').length,
      hibernated: channels.filter(c => c.status === 'HIBERNATED').length,
      deployments: deploymentGroups.length,
    };
  }, [channels, deploymentGroups]);

  const handleToggle = (deploymentName) => {
    setExpandedDeployments(prev => ({ 
      ...prev, 
      [deploymentName]: !prev[deploymentName] 
    }));
  };

  const handleRefresh = () => {
    refetch();
  };

  // Show loading state
  if (loading && channels.length === 0) {
    return (
      <Box className={classes.container} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress style={{ color: '#ff6f00' }} />
      </Box>
    );
  }

  // Show error state
  if (error && channels.length === 0) {
    return (
      <Box className={classes.container}>
        <Paper style={{ padding: '20px', backgroundColor: '#ffebee', textAlign: 'center' }}>
          <ErrorIcon style={{ color: '#c62828', fontSize: '48px' }} />
          <Typography variant="h6" style={{ color: '#c62828', marginTop: '10px' }}>
            Failed to load data
          </Typography>
          <Typography variant="body2" style={{ color: '#c62828', marginBottom: '15px' }}>
            {error}
          </Typography>
          <IconButton onClick={handleRefresh} style={{ color: '#c62828', border: '2px solid #c62828' }}>
            <Refresh />
          </IconButton>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Summary Cards */}
      <Box className={classes.summaryBox}>
        <Paper className={classes.summaryCard} style={{ backgroundColor: '#e3f2fd' }}>
          <Typography variant="h5" style={{ color: '#1565c0', fontWeight: 'bold' }}>{stats.deployments}</Typography>
          <Typography variant="caption" style={{ color: '#1565c0' }}>Deployments</Typography>
        </Paper>
        <Paper className={classes.summaryCard} style={{ backgroundColor: '#e8f5e9' }}>
          <Typography variant="h5" style={{ color: '#2e7d32', fontWeight: 'bold' }}>{stats.active}</Typography>
          <Typography variant="caption" style={{ color: '#2e7d32' }}>Active</Typography>
        </Paper>
        <Paper className={classes.summaryCard} style={{ backgroundColor: '#fff3e0' }}>
          <Typography variant="h5" style={{ color: '#e65100', fontWeight: 'bold' }}>{stats.deploying}</Typography>
          <Typography variant="caption" style={{ color: '#e65100' }}>Deploying</Typography>
        </Paper>
        <Paper className={classes.summaryCard} style={{ backgroundColor: '#ffebee' }}>
          <Typography variant="h5" style={{ color: '#c62828', fontWeight: 'bold' }}>{stats.failed}</Typography>
          <Typography variant="caption" style={{ color: '#c62828' }}>Failed</Typography>
        </Paper>
        <Paper className={classes.summaryCard} style={{ backgroundColor: '#eceff1' }}>
          <Typography variant="h5" style={{ color: '#546e7a', fontWeight: 'bold' }}>{stats.hibernated}</Typography>
          <Typography variant="caption" style={{ color: '#546e7a' }}>Hibernated</Typography>
        </Paper>
        <Paper className={classes.summaryCard} style={{ backgroundColor: '#fafafa' }}>
          <Typography variant="h5" style={{ color: '#616161', fontWeight: 'bold' }}>{stats.total}</Typography>
          <Typography variant="caption" style={{ color: '#616161' }}>Total Channels</Typography>
        </Paper>
      </Box>

      {/* Search, Create and Refresh */}
      <Box className={classes.searchBox}>
        <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateFormOpen(true)}
            style={{ backgroundColor: '#ff6f00', color: 'white' }}
          >
            Create
          </Button>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by deployment, EID, channel, device, TX chain..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            style={{ width: '500px', backgroundColor: 'white' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: '#ff6f00' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <IconButton 
          onClick={handleRefresh} 
          disabled={loading}
          size="small"
          style={{ color: '#ff6f00', border: '1px solid #ff6f00', borderRadius: '4px', padding: '6px' }}
        >
          {loading ? <CircularProgress size={20} style={{ color: '#ff6f00' }} /> : <Refresh fontSize="small" />}
        </IconButton>
      </Box>

      {/* Create Deployment Form */}
      <CreateDeploymentForm
        open={createFormOpen}
        onClose={() => setCreateFormOpen(false)}
        onSuccess={refetch}
        onAutoDeployStart={handleAutoDeployExecutions}
        onNotify={({ message, severity }) => setSnackbar({ open: true, message, severity })}
      />

      {/* Edit Channel Dialog */}
      <EditChannelDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingChannel(null);
        }}
        channel={editingChannel}
        onSuccess={refetch}
        onNotify={({ message, severity }) => setSnackbar({ open: true, message, severity })}
      />

      {/* Deployments Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#e0e0e0' }}>
              <TableCell width="50px"></TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={() => handleSortToggle('deployment')}>
                  <strong>Deployment</strong>
                  {sortColumn === 'deployment' && sortDirection === 'asc' && <ArrowUpward style={{ fontSize: '18px', marginLeft: '8px' }} />}
                  {sortColumn === 'deployment' && sortDirection === 'desc' && <ArrowDownward style={{ fontSize: '18px', marginLeft: '8px' }} />}
                  {sortColumn !== 'deployment' && <UnfoldMore style={{ fontSize: '18px', marginLeft: '8px', color: '#999' }} />}
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={() => handleSortToggle('group')}>
                  <strong>Group</strong>
                  {sortColumn === 'group' && sortDirection === 'asc' && <ArrowUpward style={{ fontSize: '18px', marginLeft: '8px' }} />}
                  {sortColumn === 'group' && sortDirection === 'desc' && <ArrowDownward style={{ fontSize: '18px', marginLeft: '8px' }} />}
                  {sortColumn !== 'group' && <UnfoldMore style={{ fontSize: '18px', marginLeft: '8px', color: '#999' }} />}
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleSortToggle('channels')}>
                  <strong>Channels</strong>
                  {sortColumn === 'channels' && sortDirection === 'asc' && <ArrowUpward style={{ fontSize: '18px', marginLeft: '8px' }} />}
                  {sortColumn === 'channels' && sortDirection === 'desc' && <ArrowDownward style={{ fontSize: '18px', marginLeft: '8px' }} />}
                  {sortColumn !== 'channels' && <UnfoldMore style={{ fontSize: '18px', marginLeft: '8px', color: '#999' }} />}
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleSortToggle('status')}>
                  <strong>Status</strong>
                  {sortColumn === 'status' && sortDirection === 'asc' && <ArrowUpward style={{ fontSize: '18px', marginLeft: '8px' }} />}
                  {sortColumn === 'status' && sortDirection === 'desc' && <ArrowDownward style={{ fontSize: '18px', marginLeft: '8px' }} />}
                  {sortColumn !== 'status' && <UnfoldMore style={{ fontSize: '18px', marginLeft: '8px', color: '#999' }} />}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDeployments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((deployment) => {
              const isExpanded = expandedDeployments[deployment.deployment_name];
              const hasErrors = deployment.channels.some(ch =>
                ch.status === 'FAILED' || ch.outputs?.some(o => o.error_message)
              );
              const nonHibernatedChannels = deployment.channels.filter(ch => ch.status !== 'HIBERNATED');
              const hasHibernated = deployment.channels.some(ch => ch.status === 'HIBERNATED');
              const allHibernated = deployment.channels.every(ch => ch.status === 'HIBERNATED');
              const allActive = nonHibernatedChannels.length > 0 && nonHibernatedChannels.every(ch =>
                ch.status === 'ACTIVE' && ch.outputs?.length > 0 && ch.outputs.every(o => o.status === 'CREATED')
              );
              const isPartial = hasHibernated && !allHibernated && allActive && !hasErrors;

              return (
                <React.Fragment key={deployment.deployment_name}>
                  {/* Deployment Row */}
                  <TableRow className={classes.deploymentRow}>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleToggle(deployment.deployment_name)}>
                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <strong>{deployment.deployment_name}</strong>
                        {hasErrors && (
                          <Tooltip title="Has errors">
                            <ErrorIcon className={classes.errorIcon} />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="caption" color="textSecondary" style={{ fontSize: '10px' }}>
                        {deployment.eid}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={deployment.group_name} 
                        size="small"
                        style={{ fontSize: '10px', height: '22px' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={deployment.channels.length} 
                        size="small"
                        style={{ 
                          backgroundColor: '#ff6f00', 
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: '28px'
                        }} 
                      />
                    </TableCell>
                    <TableCell align="center">
                      {allActive && !hasHibernated ? (
                        <Chip label="Active" size="small" className={classes.statusChip}
                          style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                      ) : hasErrors ? (
                        <Chip label="Has Errors" size="small" className={classes.statusChip}
                          style={{ backgroundColor: '#ffebee', color: '#c62828' }} />
                      ) : allHibernated ? (
                        <Chip label="Hibernated" size="small" className={classes.statusChip}
                          style={{ backgroundColor: '#eceff1', color: '#546e7a' }} />
                      ) : isPartial ? (
                        <Chip label="Partial" size="small" className={classes.statusChip}
                          style={{ backgroundColor: '#eceff1', color: '#546e7a' }} />
                      ) : (
                        <Chip label="In Progress" size="small" className={classes.statusChip}
                          style={{ backgroundColor: '#fff3e0', color: '#e65100' }} />
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded Channels */}
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box className={classes.nestedTable}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Channel</strong></TableCell>
                                <TableCell><strong>Device</strong></TableCell>
                                <TableCell><strong>TX Chain</strong></TableCell>
                                <TableCell><strong>Outputs</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {deployment.channels.map((channel) => {
                                const statusColor = getStatusColor(channel.status);
                                const hasError = channel.outputs?.some(o => o.error_message);
                                const isStartingDeploy = deployingChannels[channel.channel_id]; // API call in progress
                                const isStartingHibernate = hibernatingChannels[channel.channel_id]; // API call in progress
                                const trackedExecution = getExecution(channel.channel_id);
                                const isTrackingDeploy = trackedExecution?.action === 'deploy';
                                const isTrackingHibernate = trackedExecution?.action === 'hibernate';
                                const isBusy = isStartingDeploy || isStartingHibernate || !!trackedExecution;
                                const isDeploying = isStartingDeploy || isTrackingDeploy;
                                const isHibernating = isStartingHibernate || isTrackingHibernate;
                                const isEnabled = enabledChannels[channel.channel_id] || false;
                                const canAct = isEnabled && !isBusy;
                                const isHibernated = channel.status === 'HIBERNATED';

                                return (
                                  <TableRow
                                    key={channel.channel_id}
                                    className={classes.channelRow}
                                    style={{ backgroundColor: isHibernated ? '#f5f5f5' : 'white' }}
                                  >
                                    <TableCell style={{ fontSize: '12px' }}>
                                      {channel.channel_name}
                                    </TableCell>
                                    <TableCell style={{ fontSize: '12px' }}>
                                      {channel.device_id}
                                    </TableCell>
                                    <TableCell style={{ fontSize: '12px' }}>
                                      {channel.tx_chain_name}
                                    </TableCell>
                                    <TableCell style={{ fontSize: '11px' }}>
                                      {channel.srtOutputs.length === 0 && !channel.udpOutput ? (
                                        <span style={{ color: '#999' }}>No outputs</span>
                                      ) : (
                                        // Grid, not stacked flex rows: every line emits the same
                                        // five cells so the type, badge and endpoint columns line
                                        // up regardless of how many outputs a channel has. UDP has
                                        // no region or priority, so those cells hold a muted dash
                                        // rather than collapsing and shifting the endpoint left.
                                        <Box className={classes.outputGrid}>
                                          {channel.srtOutputs.map((output) => {
                                            const outputRegionColor = getRegionColor(output.region);
                                            const outputPriorityColor = getPriorityColor(output.stream_priority);

                                            return (
                                              <React.Fragment key={output.output_id}>
                                                <span className={`${classes.outputType} ${classes.outputTypeSrt}`}>SRT</span>
                                                <Chip
                                                  label={output.stream_priority?.toUpperCase() || '-'}
                                                  size="small"
                                                  className={classes.priorityChip}
                                                  style={{ backgroundColor: outputPriorityColor.bg, color: outputPriorityColor.color }}
                                                />
                                                <Chip
                                                  label={output.region?.toUpperCase() || '-'}
                                                  size="small"
                                                  className={classes.regionChip}
                                                  style={{ backgroundColor: outputRegionColor.bg, color: outputRegionColor.color }}
                                                />
                                                <Tooltip
                                                  title={
                                                    <span style={{ fontSize: '11px' }}>
                                                      {output.output_name}
                                                      <br />
                                                      stream_id: {output.stream_id || '-'}
                                                      <br />
                                                      status: {output.status}
                                                    </span>
                                                  }
                                                >
                                                  <span className={classes.outputEndpoint}>
                                                    {output.srt_host}:{output.srt_port}
                                                  </span>
                                                </Tooltip>
                                                {output.error_message ? (
                                                  <Tooltip title={output.error_message}>
                                                    <ErrorIcon className={classes.errorIcon} style={{ fontSize: '14px' }} />
                                                  </Tooltip>
                                                ) : (
                                                  <span />
                                                )}
                                              </React.Fragment>
                                            );
                                          })}

                                          {channel.srtOutputs.length === 0 && (
                                            <>
                                              <span className={`${classes.outputType} ${classes.outputTypeAbsent}`}>SRT</span>
                                              <span className={classes.outputNotApplicable}>—</span>
                                              <span className={classes.outputNotApplicable}>—</span>
                                              <span style={{ color: '#999' }}>No SRT output</span>
                                              <span />
                                            </>
                                          )}

                                          {channel.udpOutput && (
                                            <>
                                              <span className={`${classes.outputType} ${classes.outputTypeUdp}`}>UDP</span>
                                              <span className={classes.outputNotApplicable}>—</span>
                                              <span className={classes.outputNotApplicable}>—</span>
                                              <Tooltip
                                                title={
                                                  <span style={{ fontSize: '11px' }}>
                                                    {channel.udpOutput.output_name}
                                                    <br />
                                                    status: {channel.udpOutput.status}
                                                  </span>
                                                }
                                              >
                                                <span className={classes.outputEndpoint}>
                                                  {channel.udpOutput.multicast_ip}:{channel.udpOutput.port}
                                                </span>
                                              </Tooltip>
                                              {channel.udpOutput.error_message ? (
                                                <Tooltip title={channel.udpOutput.error_message}>
                                                  <ErrorIcon className={classes.errorIcon} style={{ fontSize: '14px' }} />
                                                </Tooltip>
                                              ) : (
                                                <span />
                                              )}
                                            </>
                                          )}
                                        </Box>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={channel.status} 
                                        size="small"
                                        className={classes.statusChip}
                                        style={{ backgroundColor: statusColor.bg, color: statusColor.color }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: '4px' }}>
                                        <Checkbox
                                          checked={isEnabled}
                                          onChange={() => handleToggleEnabled(channel.channel_id)}
                                          disabled={isBusy}
                                          size="small"
                                          color="default"
                                          style={{ padding: '2px' }}
                                        />
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => handleEdit(channel)}
                                          disabled={!isEnabled || isBusy}
                                          style={{ 
                                            fontSize: '11px', 
                                            padding: '4px 8px',
                                            minWidth: '70px',
                                            height: '32px',
                                            borderColor: canAct ? '#1565c0' : '#ccc',
                                            color: canAct ? '#1565c0' : '#999',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          <Edit style={{ fontSize: '14px', marginRight: '2px' }} />
                                          Edit
                                        </Button>
                                        <Button
                                          size="small"
                                          variant={isDeploying ? "contained" : "outlined"}
                                          onClick={() => !isBusy && handleDeploy(channel)}
                                          disabled={!isEnabled && !isDeploying}
                                          style={{ 
                                            fontSize: '11px', 
                                            padding: '4px 8px',
                                            minWidth: '100px',
                                            height: '32px',
                                            borderColor: (canAct || isDeploying) ? '#ff6f00' : '#ccc',
                                            color: isDeploying ? 'white' : (canAct ? '#ff6f00' : '#999'),
                                            backgroundColor: isDeploying ? '#ff6f00' : 'transparent',
                                            pointerEvents: isBusy ? 'none' : 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          {isDeploying ? (
                                            <Box display="flex" alignItems="center" justifyContent="center">
                                              <CircularProgress size={20} style={{ marginRight: '6px', color: 'white' }} />
                                              <span style={{ fontSize: '11px' }}>Deploying</span>
                                            </Box>
                                          ) : (
                                            <>
                                              <PlayArrow style={{ fontSize: '14px', marginRight: '2px' }} />
                                              Deploy
                                            </>
                                          )}
                                        </Button>
                                        <Button
                                          size="small"
                                          variant={isHibernating ? "contained" : "outlined"}
                                          onClick={() => !isBusy && handleHibernate(channel)}
                                          disabled={!isEnabled && !isHibernating}
                                          style={{ 
                                            fontSize: '11px', 
                                            padding: '4px 8px',
                                            minWidth: '100px',
                                            height: '32px',
                                            borderColor: (canAct || isHibernating) ? '#546e7a' : '#ccc',
                                            color: isHibernating ? 'white' : (canAct ? '#546e7a' : '#999'),
                                            backgroundColor: isHibernating ? '#546e7a' : 'transparent',
                                            pointerEvents: isBusy ? 'none' : 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          {isHibernating ? (
                                            <Box display="flex" alignItems="center" justifyContent="center">
                                              <CircularProgress size={20} style={{ marginRight: '6px', color: 'white' }} />
                                              <span style={{ fontSize: '11px' }}>Hibernating</span>
                                            </Box>
                                          ) : (
                                            <>
                                              <Pause style={{ fontSize: '14px', marginRight: '2px' }} />
                                              Hibernate
                                            </>
                                          )}
                                        </Button>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={sortedDeployments.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
      />

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity} 
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Overview;
